/**
 * useOrderManagementReturns — ViewModel for /order-management/returns.
 *
 * Per SOP 02. Read-only paginated list joined with customer name /
 * email / phone on the server.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export interface OrderReturnListDto {
  readonly id: string
  readonly order_id: string
  readonly reason: string
  readonly status: string
  readonly requested_at: string
  readonly customer_id: string
  readonly customer_first_name: string | null
  readonly customer_last_name: string | null
  readonly customer_email: string
  readonly customer_phone: string | null
  /** Tenant the return's parent order was placed against. Drives
   *  the org dropdown on the page (Kiran 2026-06-10). */
  readonly organisation_id: string
  readonly organisation_name: string | null
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: OrderReturnListDto[]
    readonly total: number
  }
}

export function useOrderManagementReturns(organisationId: string | null = null): {
  readonly returns: ReadonlyArray<OrderReturnListDto>
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /** Advance the return to a target status (Accept / Reject /
   *  Pickup / Received / Refund / Refunded). Kiran 2026-06-17. */
  readonly transition: (
    returnId: string,
    nextStatus: string,
    notes?: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
} {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<OrderReturnListDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const orgParam = organisationId !== null && organisationId !== ''
          ? `&organisation_id=${encodeURIComponent(organisationId)}`
          : ''
        const url = `${APIROUTES.ORDERMANAGEMENTRETURNS}?page=${page}&pageSize=${pageSize}${orgParam}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch, organisationId]
    ),
    // Filter-deps: page resets to 1 when the org filter changes.
    deps: [organisationId]
  })

  const transition = useCallback(
    async (returnId: string, nextStatus: string, notes?: string) => {
      const body: { status: string; notes?: string } = { status: nextStatus }
      if (notes !== undefined) body.notes = notes
      const r = await mutateJson<{ status: string }>(
        authedFetch,
        fillPath(APIROUTES.ORDERMANAGEMENTRETURNTRANSITION, { id: returnId }),
        'POST',
        body
      )
      if (r.ok) {
        // 1-second delay for the consumer to drain the queue message
        // (Kiran 2026-06-17) so the operator sees the new return-row
        // status on first refresh.
        await new Promise((resolve) => setTimeout(resolve, 1000))
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  return {
    returns: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    transition
  }
}
